import Ajv from 'ajv';
import ajvErrors from 'ajv-errors';
import addFormats from 'ajv-formats';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { checkCondition } from './useConditionalFields.tsx';
import { useRegistrationStore } from '../store/store.ts';
import { FieldType, TFlow, TInputField } from '../types/types.ts';
import { isEsPasswordValid } from '../utils/esPasswordValidation.ts';
import { GtmEvents } from '../utils/gtm/gtm.ts';
import { isPasswordValid } from '../utils/passwordValidation.ts';
import { flattenFields } from '../utils/utils.ts';

type TField = {
  name: string;
  fieldType?: string;
  validation?: object;
  validationText?: string;
  gtmPayload?: Record<string, string>;
  errorMessage?: string;
  untickedRequirementsError?: string;
};

const useFieldValidator = (config: TFlow) => {
  const { metadata, formData, customValidations, errors, conditions, setErrors } = useRegistrationStore();
  const { step: stepParam } = useParams<{ step: string }>();
  const step = useMemo(() =>
    stepParam
      ? +stepParam.split('_')[1] || 1
      : 1,
  [stepParam]);
  const currentStepConfig = config?.steps[step]
  const topLevelFields = currentStepConfig?.fields;
  const allFields = flattenFields(topLevelFields);

  const renderedFields = allFields.filter(field =>
    !(field as TInputField).dependsOn ||
    (field as TInputField)?.dependsOn?.every(dep =>
      checkCondition(dep, conditions, formData, metadata),
    ),
  );

  const ajv = new Ajv({ allErrors: true });
  addFormats(ajv);
  ajvErrors(ajv);

  const validateField = (field: TField) => {
    let fieldError = '';

    if (field.validation) {
      const validate = ajv.compile(field.validation);
      const valid = validate(formData?.[field.name]);

      if (!valid) {
        const error = validate.errors?.[0];
        if (error?.message) {
          fieldError = error.message;
          GtmEvents.logInputAlert(field.name, metadata?.currentStep);
        }
      }
    }

    if (field.fieldType === FieldType.PasswordInput) {
      const password = formData?.[field.name] || '';
      if (!password || !isPasswordValid(password)) {
        fieldError = field.errorMessage || 'Invalid password';
        GtmEvents.logInputAlert(field.name, metadata?.currentStep);
      }
    }

    if (field.fieldType === FieldType.EsPasswordInput) {
      const password = formData?.[field.name] || '';
      if (!password || !isEsPasswordValid(password, formData)) {
        fieldError = field.untickedRequirementsError || 'Invalid password';
        GtmEvents.logInputAlert(field.name, metadata?.currentStep);
      }
    }

    if (customValidations[field.name]) {
      fieldError = customValidations[field.name];
    }

    return fieldError;
  };

  const handleBlur = (field: TField) => {
    const hasAjvValidation = !!field.validation;
    const hasCustomValidation = !!customValidations[field.name];

    if (!hasAjvValidation && !hasCustomValidation) {
      return;
    }

    const fieldError = validateField(field);
    setErrors({ ...errors, [field.name]: fieldError });
  };

  // Validate all fields at once (e.g., on form submission)
  const validateAllFields = (fields?: TField[]) => {
    const newErrors: Record<string, string> = {};
    const fieldsToRender = fields ?? renderedFields
    fieldsToRender.forEach((field) => {
      const fieldError = validateField(field);
      if (fieldError) {
        newErrors[field.name] = fieldError;
      }
    });

    Object.entries(customValidations).forEach(([name, message]) => {
      if (message && !newErrors[name]) {
        newErrors[name] = message;
      }
    });

    setErrors({ ...errors, ...newErrors });

    const errorField = document.querySelector('[data-error="true"]');

    if (errorField) {
      (errorField as HTMLElement).focus({ preventScroll: true });
      errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return Object.keys(newErrors).length === 0;
  };

  return { errors, ajv, handleBlur, validateAllFields };
};

export default useFieldValidator;
